import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api/client";
import { firebaseAuthService } from "@/../frontend/services/firebaseAuth.service";
import { qk } from "@/lib/lib/query/keys";
import { useToast } from "@/hooks/use-toast";
import type { KnowledgeDoc } from "@/lib/types";

// Unified knowledge management hooks
export function useKnowledge(colonyId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  
  // Query for listing knowledge documents
  const query = useQuery({
    queryKey: qk.files(colonyId),
    queryFn: async () => {
      if (!colonyId) return { items: [] as KnowledgeDoc[] };
      const user = await firebaseAuthService.getCurrentUser();
      const res: any = await api.get(`/api/colony/${colonyId}/knowledge`, { 
        userId: user?.uid || ''
      });
      const items = Array.isArray((res as any)?.items) ? (res as any).items as KnowledgeDoc[] : [];
      return { items };
    },
    enabled: !!colonyId,
  });

  // Upload mutation with presign flow
  const upload = useMutation({
    mutationFn: async (file: File) => {
      const user = await firebaseAuthService.getCurrentUser();
      if (!user) throw new Error('Not authenticated');
      
      const mime = file.type || 'application/octet-stream';
      
      // Get presigned URL
      const presign: any = await api.post(`/api/colony/${colonyId}/knowledge/presign`, {
        userId: user.uid,
        filename: file.name,
        mime,
        size: file.size
      });
      
      // Upload file to presigned URL
      await fetch(presign.url, { 
        method: 'PUT', 
        body: file, 
        headers: { 'Content-Type': mime } 
      });
      
      // Commit the upload
      const commit: any = await api.post(`/api/colony/${colonyId}/knowledge/commit`, {
        userId: user.uid,
        fileKey: presign.fileKey,
        title: file.name,
        mime,
        bytes: file.size
      });
      
      return (commit.doc as KnowledgeDoc) || null;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.files(colonyId) });
      toast({ 
        title: "Uploaded", 
        description: "File uploaded successfully" 
      });
    },
    onError: (error: any) => {
      console.error("Upload error:", error);
      
      let description = "An unknown error occurred.";
      if (error && typeof error === 'object' && 'message' in error) {
        try {
          const parsedError = JSON.parse((error as any).message);
          if (parsedError.error === 'unsupported_type') {
            description = parsedError.message || `File type is not supported.`;
          } else if (parsedError.error === 'file_too_large') {
            description = parsedError.message || 'The file is too large to upload.';
          } else {
            description = parsedError.message || String(error);
          }
        } catch {
          description = (error as any).message || String(error);
        }
      } else {
        description = String(error);
      }
      
      toast({ 
        title: "Upload failed", 
        description, 
        variant: "destructive" 
      });
    },
  });

  // Delete mutation
  const del = useMutation({
    mutationFn: async (params: { docId: string; reason?: string }) => {
      const user = await firebaseAuthService.getCurrentUser();
      if (!user) throw new Error('Not authenticated');
      
      await api.delete(`/api/colony/${colonyId}/knowledge/${encodeURIComponent(params.docId)}`, { 
        userId: user.uid 
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.files(colonyId) });
      toast({ 
        title: "Deleted", 
        description: "File deleted successfully" 
      });
    },
    onError: (error) => {
      console.error("Delete error:", error);
      toast({ 
        title: "Delete failed", 
        description: String(error), 
        variant: "destructive" 
      });
    },
  });

  return { 
    query, 
    upload, 
    del,
    // Legacy aliases for backward compatibility
    data: query.data?.items || [],
    isLoading: query.isLoading,
    error: query.error
  };
}


